import { NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Home, LayoutDashboard, Wand2, FileText, Target,
  Database, Boxes, Terminal, Download, ScrollText, Building2, Shield,
  Sparkles, Upload, PanelLeftClose, PanelLeftOpen, Crown
} from "lucide-react";
import { useAuth, canAtLeast, isLockedDashboardUser, canUseAskAI } from "../lib/auth";

const ICON_PROPS = { size: 16, strokeWidth: 1.6 };

const GROUPS = [
  {
    label: "Overview",
    items: [
      { to: "/", label: "Home", icon: Home, end: true, testid: "nav-home" },
      { to: "/dashboards", label: "Dashboards", icon: LayoutDashboard, testid: "nav-dashboards" },
      { to: "/ceo-command-center", label: "CEO Command Center", icon: Crown, testid: "nav-ceo-command-center", role: "super_admin" },
    ],
  },
  {
    label: "Build",
    items: [
      { to: "/builder/new", label: "Dashboard Builder", icon: Wand2, testid: "nav-builder", role: "analyst" },
      { to: "/ai-agent", label: "AI Agent", icon: Sparkles, testid: "nav-ai-agent", role: "analyst" },
      { to: "/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
      { to: "/kpi", label: "KPI Center", icon: Target, testid: "nav-kpi" },
    ],
  },
  {
    label: "Data",
    items: [
      { to: "/data-sources", label: "Data Sources", icon: Database, testid: "nav-data-sources" },
      { to: "/datasets", label: "Datasets", icon: Boxes, testid: "nav-datasets" },
      { to: "/uploads", label: "Excel Uploads", icon: Upload, testid: "nav-uploads" },
      { to: "/sql", label: "SQL Editor", icon: Terminal, testid: "nav-sql", role: "analyst" },
    ],
  },
  {
    label: "Manage",
    items: [
      { to: "/exports", label: "Exports", icon: Download, testid: "nav-exports" },
      // ETL Admin is intentionally hidden until ETL_DB_URL / warehouse setup is completed.
      { to: "/audit", label: "Audit Logs", icon: ScrollText, testid: "nav-audit", role: "workspace_admin" },
      { to: "/workspaces", label: "Workspaces", icon: Building2, testid: "nav-workspaces", role: "super_admin" },
      { to: "/access-management", label: "Access Management", icon: Shield, testid: "nav-access-management", role: "workspace_admin" },
    ],
  },
];

export default function Sidebar() {
  const { user } = useAuth();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem("qat_sidebar_collapsed") === "1";
  });

  useEffect(() => {
    localStorage.setItem("qat_sidebar_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  if (!user) return null;


  if (isLockedDashboardUser(user)) {
    const dashId = user.assigned_dashboard_id || (user.allowed_dashboard_ids || [])[0];
    const dashPath = dashId ? `/dashboards/${dashId}` : "/dashboards";

    return (
      <aside
        className={`sidebar ${collapsed ? "sidebar-collapsed w-[72px]" : "w-[260px]"} shrink-0 flex flex-col h-screen sticky top-0 transition-all duration-200`}
        data-testid="sidebar"
      >
        <div className="px-3 pt-5 pb-4 border-b" style={{ borderColor: "var(--c-sidebar-border)" }}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"}`}>
            <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--c-accent)" }}>
              <LayoutDashboard size={16} strokeWidth={1.8} color="#fff" />
            </div>
            {!collapsed && (
              <div className="leading-tight min-w-0">
                <div className="font-heading text-[15px] font-semibold tracking-tight truncate">Client Dashboard</div>
                <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "#a1a1aa" }}>Restricted Access</div>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setCollapsed((v) => !v)}
            className="sidebar-toggle"
            title={collapsed ? "Open sidebar" : "Collapse sidebar"}
            data-testid="sidebar-toggle"
          >
            {collapsed ? <PanelLeftOpen size={15} strokeWidth={1.7} /> : <PanelLeftClose size={15} strokeWidth={1.7} />}
            {!collapsed && <span>Collapse</span>}
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto pb-4">
          <div>
            {!collapsed && <div className="nav-group-label">Dashboard</div>}
            <NavLink
              to={dashPath}
              className={({ isActive: a }) => `nav-item ${collapsed ? "collapsed" : ""} ${a || loc.pathname.startsWith(dashPath) ? "active" : ""}`}
              data-testid="nav-client-dashboard"
              title={collapsed ? "Dashboard" : undefined}
            >
              <LayoutDashboard {...ICON_PROPS} />
              {!collapsed && <span>Dashboard</span>}
            </NavLink>
          </div>
        </nav>

        <div className="px-3 py-3 border-t" style={{ borderColor: "var(--c-sidebar-border)" }}>
          <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"}`}>
            <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "#1d4ed8" }}>
              <Shield size={14} strokeWidth={1.8} color="#fff" />
            </div>
            {!collapsed && (
              <div className="leading-tight min-w-0">
                <div className="text-[12.5px] font-medium truncate">{user.name}</div>
                <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "#a1a1aa" }}>{String(user.role || "").replace("_", " ")}</div>
              </div>
            )}
          </div>
        </div>
      </aside>
    );
  }


  return (
    <aside
      className={`sidebar ${collapsed ? "sidebar-collapsed w-[72px]" : "w-[260px]"} shrink-0 flex flex-col h-screen sticky top-0 transition-all duration-200`}
      data-testid="sidebar"
    >
      <div className="px-3 pt-5 pb-4 border-b" style={{ borderColor: "var(--c-sidebar-border)" }}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"}`}>
          <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "var(--c-accent)" }}>
            <Sparkles size={16} strokeWidth={1.8} color="#fff" />
          </div>

          {!collapsed && (
            <div className="leading-tight min-w-0">
              <div className="font-heading text-[15px] font-semibold tracking-tight truncate">Camaroo Analytics</div>
              <div className="text-[10.5px] uppercase tracking-widest" style={{ color: "#a1a1aa" }}>Quantira Platform</div>
            </div>
          )}
        </div>

        {!collapsed && canUseAskAI(user) && (
          <button
            type="button"
            className="sidebar-ask-ai-btn"
            data-testid="sidebar-ask-ai"
            onClick={() => window.dispatchEvent(new CustomEvent("quantira:open-ask-ai"))}
          >
            <Sparkles size={16} strokeWidth={1.9} />
            <span>Ask AI</span>
          </button>
        )}

        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          className="sidebar-toggle"
          title={collapsed ? "Open sidebar" : "Collapse sidebar"}
          data-testid="sidebar-toggle"
        >
          {collapsed ? <PanelLeftOpen size={15} strokeWidth={1.7} /> : <PanelLeftClose size={15} strokeWidth={1.7} />}
          {!collapsed && <span>Collapse</span>}
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto pb-4">
        {GROUPS.map((g) => {
          const visible = g.items.filter((it) => !it.role || canAtLeast(user, it.role));
          if (visible.length === 0) return null;
          return (
            <div key={g.label}>
              {!collapsed && <div className="nav-group-label">{g.label}</div>}
              {visible.map((it) => {
                const Icon = it.icon;
                const isActive = it.end ? loc.pathname === it.to : loc.pathname.startsWith(it.to);
                return (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    className={({ isActive: a }) => `nav-item ${collapsed ? "collapsed" : ""} ${(a || isActive) ? "active" : ""}`}
                    data-testid={it.testid}
                    title={collapsed ? it.label : undefined}
                  >
                    <Icon {...ICON_PROPS} />
                    {!collapsed && <span>{it.label}</span>}
                  </NavLink>
                );
              })}
            </div>
          );
        })}
      </nav>

      <div className="px-3 py-3 border-t" style={{ borderColor: "var(--c-sidebar-border)" }}>
        <div className={`flex items-center ${collapsed ? "justify-center" : "gap-2.5"}`}>
          <div className="w-8 h-8 rounded-md flex items-center justify-center shrink-0" style={{ background: "#1d4ed8" }}>
            <Shield size={14} strokeWidth={1.8} color="#fff" />
          </div>
          {!collapsed && (
            <div className="leading-tight min-w-0">
              <div className="text-[12.5px] font-medium truncate">{user.name}</div>
              <div className="text-[10.5px] uppercase tracking-wider" style={{ color: "#a1a1aa" }}>{user.role.replace("_", " ")}</div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
}
